import { pcmDurationMs, SAMPLE_RATE } from "@transcription/wav";

const MAX_BYTES = SAMPLE_RATE * 2 * 90;
const TAIL_MS = 200;
const JOIN_BYTES = (SAMPLE_RATE * 2 * 250) / 1000;

// RMS on the 0..32767 scale. Deliberately not adaptive: a noise-floor estimator would mistrain on clinic chatter.
const SPEECH_RMS = 700;

export function isSpeech(chunk: ArrayBuffer): boolean {
  const samples = new Int16Array(chunk);
  if (samples.length === 0) return false;
  let sum = 0;
  for (const sample of samples) sum += sample * sample;
  return Math.sqrt(sum / samples.length) >= SPEECH_RMS;
}

/** Memory-only PCM16/16k collector; consumes the echo-cancelled microphone, including during playback. */
export class InterviewSample {
  private accepted: ArrayBuffer[] = [];
  private candidate: ArrayBuffer[] = [];
  private acceptedBytes = 0;
  private candidateBytes = 0;
  private acceptedSpeechMs = 0;
  private candidateSpeechMs = 0;
  private silenceMs = 0;
  private recognized = false;

  observe(chunk: ArrayBuffer): void {
    // End on microphone silence, never on Brian's turn: backchannels can overlap.
    // Leave time for recognition deltas to arrive before committing the segment.
    if (this.silenceMs >= 3000) this.finishTurn();
    const betweenTurns = this.acceptedBytes > 0 ? JOIN_BYTES : 0;
    const resumesAfterTrim =
      this.candidateBytes > 0 && this.silenceMs > TAIL_MS && isSpeech(chunk);
    const joinBytes = resumesAfterTrim ? JOIN_BYTES : 0;
    const remaining =
      MAX_BYTES -
      this.acceptedBytes -
      this.candidateBytes -
      betweenTurns -
      joinBytes;
    const size = Math.min(chunk.byteLength, remaining);
    if (size < 2) return;
    const bounded = chunk.slice(0, size - (size % 2));
    const durationMs = pcmDurationMs(bounded.byteLength);
    const speech = isSpeech(bounded);
    if (speech) {
      if (resumesAfterTrim) {
        this.candidate.push(new ArrayBuffer(JOIN_BYTES));
        this.candidateBytes += JOIN_BYTES;
      }
      this.candidateSpeechMs += durationMs;
      this.silenceMs = 0;
    } else {
      this.silenceMs += durationMs;
    }
    if (speech || (this.candidateSpeechMs > 0 && this.silenceMs <= TAIL_MS)) {
      this.candidate.push(bounded);
      this.candidateBytes += bounded.byteLength;
    }
  }

  // An energy threshold alone cannot distinguish speech from household noise.
  recognizeSpeech(): void {
    this.recognized = true;
  }

  finishTurn(): void {
    if (this.recognized && this.candidateSpeechMs > 0) {
      if (this.acceptedBytes > 0) {
        this.accepted.push(new ArrayBuffer(JOIN_BYTES));
        this.acceptedBytes += JOIN_BYTES;
      }
      this.accepted.push(...this.candidate);
      this.acceptedBytes += this.candidateBytes;
      this.acceptedSpeechMs += this.candidateSpeechMs;
    }
    this.candidate = [];
    this.candidateBytes = 0;
    this.candidateSpeechMs = 0;
    this.silenceMs = 0;
    this.recognized = false;
  }

  get speechSeconds(): number {
    return (
      (this.acceptedSpeechMs + (this.recognized ? this.candidateSpeechMs : 0)) /
      1000
    );
  }

  take(): ArrayBuffer[] {
    this.finishTurn();
    const chunks = this.accepted;
    this.clear();
    return chunks;
  }

  clear(): void {
    this.accepted = [];
    this.candidate = [];
    this.acceptedBytes = 0;
    this.candidateBytes = 0;
    this.acceptedSpeechMs = 0;
    this.candidateSpeechMs = 0;
    this.silenceMs = 0;
    this.recognized = false;
  }
}
